import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Alert,
  Button,
  Divider,
  Avatar,
  Paper,
  Stack,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import { Info, Login, ContentCopy } from "@mui/icons-material";
import { useCustom } from "@refinedev/core";
import { useTranslation } from "../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";

const benefits = [
  "benefits.items.returnRequests",
  "benefits.items.refundProcessing",
  "benefits.items.criticalNotifications",
  "benefits.items.customerMessages",
];

const StepDivider = ({ stepNumber }: { stepNumber: number }) => (
  <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
    <Divider sx={{ width: "100%", position: "relative" }}>
      <Avatar
        sx={{
          bgcolor: "primary.main",
          color: "white",
          width: 40,
          height: 40,
          fontSize: "16px",
          fontWeight: "bold",
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {stepNumber}
      </Avatar>
    </Divider>
  </Box>
);

export const AmazonNotificationSettings = () => {
  const [copied, setCopied] = useState(false);
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.stores.mailNotification.amazonNotificationSettings",
  );
  const { selectedCountry } = useMarketplaceStore((state) => state);

  const { data } = useCustom({
    url: `store/${selectedCountry?.id}/mail`,
    method: "get",
  });
  const mail = data?.data;

  const steps = useMemo(
    () => [
      {
        title: "steps.step1.title",
        description: "steps.step1.description",
        buttonText: "steps.step1.buttonText",
        buttonLink: selectedCountry?.sellerCentralURL,
      },
      {
        title: "steps.step2.title",
        description: "steps.step2.description",
        buttonText: "steps.step2.buttonText",
        buttonLink: selectedCountry?.sellerCentralURL
          ? `${selectedCountry.sellerCentralURL}/notifications/preferences`
          : undefined,
        additionalSteps: [
          "steps.step2.additionalSteps.accountNotifications",
          "steps.step2.additionalSteps.businessUpdates",
        ],
      },
      {
        title: "steps.step3.title",
        description: "steps.step3.description",
        additionalSteps: ["steps.step3.additionalSteps.emailSection"],
      },
      {
        title: "steps.step4.title",
        description: "steps.step4.description",
        additionalSteps: ["steps.step4.additionalSteps.returnClaims"],
      },
      {
        title: "steps.step5.title",
        description: "steps.step5.description",
        additionalSteps: ["steps.step5.additionalSteps.messagingOptions"],
      },
    ],
    [selectedCountry?.sellerCentralURL],
  );

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(String(mail));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Email kopyalanamadı:", error);
    }
  };

  const handleButtonClick = (buttonLink: string | undefined) => {
    window.open(buttonLink, "_blank", "noopener,noreferrer");
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Uyarı Mesajı */}
      <Alert
        severity="info"
        icon={<Info />}
        sx={{
          mb: 4,
          borderRadius: 2,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <Typography variant="body1">{translate("warning")}</Typography>
      </Alert>

      {/* Ana İçerik */}
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 4 }}>
        {/* Faydalar Bölümü */}
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 3, color: "primary.main", fontWeight: "bold" }}
            >
              {translate("benefits.title")}
            </Typography>

            <Stack spacing={2}>
              {benefits.map((benefit) => (
                <Paper
                  key={benefit}
                  sx={{
                    p: 2,
                  }}
                  variant="outlined"
                  elevation={0}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {translate(benefit)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Email Bölümü */}
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 3, color: "primary.main", fontWeight: "bold" }}
            >
              {translate("email.title")}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {translate("email.description")}
            </Typography>

            <Alert
              severity="info"
              sx={{
                mb: 2,
                cursor: "pointer",
                borderRadius: 2,
                transition: "all 0.2s",
              }}
              onClick={handleCopyEmail}
              action={
                <Button
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyEmail}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {translateWithoutPrefix("buttons.copy")}
                </Button>
              }
            >
              {copied ? (
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="success.main"
                >
                  {translateWithoutPrefix("common.copied")}
                </Typography>
              ) : (
                <Box>
                  <Typography variant="body2" component="span" fontWeight="500">
                    {translate("email.label")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    fontWeight="bold"
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: "rgba(0,0,0,0.05)",
                      borderRadius: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {String(mail)}
                  </Typography>
                </Box>
              )}
            </Alert>
          </CardContent>
        </Card>
      </Box>

      {steps.map((step, index) => (
        <Box key={step.title}>
          <StepDivider stepNumber={index + 1} />

          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 3, color: "primary.main", fontWeight: "bold" }}
              >
                {translate(step.title)}
              </Typography>

              <Typography sx={{ fontSize: "16px", mb: 3, lineHeight: 1.6 }}>
                {translate(step.description, {
                  countryName: selectedCountry?.countryCode ?? "",
                })}
              </Typography>

              {step.buttonText && step.buttonLink && (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Login />}
                    onClick={() => handleButtonClick(step.buttonLink)}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "16px",
                      fontWeight: "bold",
                      transition: "all 0.2s",
                      textTransform: "capitalize",
                    }}
                  >
                    {translate(step.buttonText)}
                  </Button>
                </Box>
              )}

              {step.additionalSteps && (
                <Stack spacing={2} sx={{ mb: 3 }}>
                  {step.additionalSteps.map((additionalStep) => (
                    <Paper key={additionalStep} variant="outlined">
                      <Typography
                        sx={{
                          fontSize: "16px",
                          lineHeight: 1.6,
                          pl: 2,
                          py: 1,
                          borderRadius: 1,
                        }}
                      >
                        {translate(additionalStep)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Box
                  component="img"
                  src={`/images/amazon-steps/${index + 1}.png`}
                  alt={translate(step.title)}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 400,
                    borderRadius: 2,
                  }}
                  onError={(e) => {
                    console.warn(
                      `Image not found: /images/amazon-steps/${index + 1}.png`,
                    );
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      ))}
    </Container>
  );
};
